import { api, ApiError, uploadPhoto, ALLOWED_IMAGE_TYPES } from "../api.js";
import { esc, haversineKm } from "../utils.js";
import { navigate } from "../app.js";
import { searchPickerHtml, attachSearchPicker, pickerValue } from "../searchPicker.js";
import { getCurrentUser } from "../auth.js";
import { unitsOf } from "../units.js";
import { diveFieldHtml, attachDiveField } from "../diveField.js";

// A dive's site, from its log, fills in an empty dive site field when a
// dive site is this close to it.
const SITE_FROM_LOG_M = 100;

const MAX_PHOTOS = 10; // mirrors _MAX_PHOTOS_PER_POST in app/posts/routes.py

/** Doubles as both "make a new post" (#/create-post) and "edit an
 * existing one" (#/post/:id/edit) — params.id, when present,
 * switches this into edit mode and pre-fills the form. */
export async function render(container, params, query) {
  const editingId = params?.id;
  const initialDivePlanId = query?.get("dive_plan_id");
  const initialDiveSiteId = query?.get("dive_site_id");
  const initialCommunityId = query?.get("community_id");
  let existing = null;

  if (editingId) {
    container.innerHTML = `<div class="text-center py-5"><div class="spinner-border text-primary"></div></div>`;
    try {
      existing = await api.get(`/posts/${editingId}`);
    } catch (err) {
      container.innerHTML = `<div class="dc-empty"><i class="bi bi-exclamation-triangle"></i><div>Post not found.</div></div>`;
      return;
    }
  }

  container.innerHTML = `
    <h1 class="display-heading h3 mb-4">${editingId ? "Edit Post" : "New Post"}</h1>
    <form id="post-form">
      ${initialDivePlanId
        ? `<div class="alert alert-light border d-flex align-items-start gap-2 mb-3" id="post-context">
             <i class="bi bi-calendar-event mt-1"></i>
             <div class="small">
               <div>Posting about <span class="fw-semibold" id="post-context-title">this dive event</span>.</div>
               <div class="text-slate">The dive site and cohort below came from the event — change them if you like.</div>
             </div>
           </div>`
        : ""}
      <div class="mb-3">
        <textarea class="form-control" id="post-content" rows="5" placeholder="What's on your mind, diver?" required>${existing ? esc(existing.content) : ""}</textarea>
        <div class="form-text text-success" id="post-content-from-log" hidden>From your dive log's notes. Edit them before you post: anyone who can see the post will see them.</div>
      </div>
      <div class="mb-3">
        ${searchPickerHtml("post-site", {
          label: "Tag a dive site (optional)",
          placeholder: "Type to search dive sites",
          value: existing?.dive_site?.id ?? initialDiveSiteId ?? "",
          valueLabel: existing?.dive_site?.name ?? "",
          hint: "Leave empty for no site.",
        })}
        <div class="form-text text-success" id="post-site-from-log" hidden></div>
      </div>
      <div class="mb-3">
        ${searchPickerHtml("post-community", {
          label: "Post to a cohort (optional)",
          placeholder: "Type to search your cohorts",
          value: existing?.community?.id ?? initialCommunityId ?? "",
          valueLabel: existing?.community?.name ?? "",
          // Says which cohorts are searchable. Without it, typing the
          // name of a cohort you know exists and getting "No matches"
          // reads as a broken search rather than a rule.
          hint: "Only cohorts you have joined. Leave empty to post to the public feed.",
        })}
      </div>
      <div class="mb-3">
        <label class="form-label" for="post-photos">Photos (optional)</label>
        <input class="form-control" type="file" id="post-photos"
               accept="${ALLOWED_IMAGE_TYPES.join(",")}" multiple />
        <div class="form-text">Up to ${MAX_PHOTOS} images — JPEG, PNG, WebP or GIF.</div>
        <div id="photo-previews" class="d-flex flex-wrap gap-2 mt-2"></div>
      </div>
      ${diveFieldHtml()}
      <div id="post-error" class="text-danger small mb-3 d-none"></div>
      <div class="d-flex gap-2">
        <button type="submit" class="btn btn-primary" id="post-submit">${editingId ? "Update" : "Post"}</button>
        <button type="button" class="btn btn-outline-secondary" id="post-cancel">Cancel</button>
      </div>
    </form>`;

  // Assigned below, once the pickers are wired; a dive chosen before then
  // simply doesn't fill in the site.
  let sitePicker = null;

  /** An empty dive site field takes the dive site within SITE_FROM_LOG_M of
   * where the log says the dive was, if there is one. The position is sent
   * only to find it: it isn't stored, and isn't part of the dive. */
  async function siteFromLog(dive) {
    if (!dive.position || !sitePicker || pickerValue(container, "post-site") != null) return;
    const { latitude, longitude } = dive.position;
    let nearest;
    try {
      ({ items: [nearest] } = await api.getPage("/dive-sites", { query: { lat: latitude, lng: longitude }, limit: 1 }));
    } catch (_) {
      return; // a convenience: without it, the field stays as it was for the diver to fill
    }
    if (!nearest || pickerValue(container, "post-site") != null) return;
    if (haversineKm(latitude, longitude, nearest.latitude, nearest.longitude) * 1000 > SITE_FROM_LOG_M) return;
    sitePicker.choose(nearest);
    const note = container.querySelector("#post-site-from-log");
    note.textContent = `Dive site from your dive log: ${nearest.name}. Change it if that's not where you dived.`;
    note.hidden = false;
  }

  /** An empty post takes the dive's notes from the log, for the diver to
   * read and edit. They stay on this computer unless posted as its text. */
  function textFromLog(dive) {
    const content = container.querySelector("#post-content");
    if (!dive.notes || content.value.trim()) return;
    content.value = dive.notes;
    container.querySelector("#post-content-from-log").hidden = false;
  }

  const diveField = attachDiveField(container, {
    existing: existing?.dive_profile ?? null,
    units: unitsOf(getCurrentUser()),
    onLogDive: (dive) => {
      textFromLog(dive);
      siteFromLog(dive);
    },
  });

  if (initialDivePlanId) {
    try {
      const plan = await api.get(`/dive-plans/${initialDivePlanId}`);
      container.querySelector("#post-context-title").textContent = plan.title;
      // A dive log opens on the dive from the event's day, in the diver's time zone.
      const day = new Date(plan.planned_date);
      if (!Number.isNaN(day.getTime())) {
        const pad = (n) => String(n).padStart(2, "0");
        diveField.setEventDay(`${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`);
      }
    } catch (_) {
      /* non-fatal: the generic wording above still makes sense */
    }
  }

  // Searched rather than pre-loaded: the list endpoints paginate, so a
  // dropdown of every site silently stopped at the first page.
  sitePicker = attachSearchPicker(container, {
    id: "post-site",
    path: "/dive-sites",
    // Their own choice now, not the log's.
    onChange: () => { container.querySelector("#post-site-from-log").hidden = true; },
  });
  const cohortPicker = attachSearchPicker(container, {
    id: "post-community",
    path: "/cohorts",
    // Only your own: the API will not let you post into a cohort you do
    // not belong to, so listing the others just invites a rejection.
    query: { mine: 1 },
  });

  // Two separate lists because they mean different things to the API on
  // submit: `keptPhotos` are rows that already exist (sent back as
  // photo_ids so the server knows which ones NOT to detach), while
  // `pendingFiles` still have to be uploaded to object storage before we
  // have an object_key to send as photo_keys.
  const keptPhotos = existing?.photos ? [...existing.photos] : [];
  const pendingFiles = [];
  const photoInput = container.querySelector("#post-photos");
  const previewsEl = container.querySelector("#photo-previews");

  // Object URLs are a document-lifetime resource, so every createObjectURL
  // here is matched by a revoke — on removal, after submit, or when the
  // user navigates away without posting.
  function releasePreviews() {
    pendingFiles.forEach((p) => URL.revokeObjectURL(p.previewUrl));
  }
  window.addEventListener("hashchange", releasePreviews, { once: true });

  function thumbHtml(src, kind, index, label) {
    return `
      <div class="position-relative" style="width:96px;height:96px;">
        <img src="${esc(src)}" alt="${esc(label)}"
             class="rounded border w-100 h-100" style="object-fit:cover;" />
        <button type="button"
                class="btn btn-sm btn-danger position-absolute top-0 end-0 p-0 lh-1 rounded-circle"
                style="width:20px;height:20px;"
                data-remove-kind="${kind}" data-remove-index="${index}"
                aria-label="Remove ${esc(label)}">&times;</button>
      </div>`;
  }

  function renderPreviews() {
    previewsEl.innerHTML = [
      ...keptPhotos.map((p, i) => thumbHtml(p.image_url, "kept", i, "attached photo")),
      ...pendingFiles.map((p, i) => thumbHtml(p.previewUrl, "pending", i, p.file.name)),
    ].join("");

    previewsEl.querySelectorAll("[data-remove-kind]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const index = Number(btn.dataset.removeIndex);
        if (btn.dataset.removeKind === "kept") {
          keptPhotos.splice(index, 1);
        } else {
          URL.revokeObjectURL(pendingFiles[index].previewUrl);
          pendingFiles.splice(index, 1);
        }
        renderPreviews();
      });
    });
  }

  photoInput.addEventListener("change", () => {
    const errorEl = container.querySelector("#post-error");
    const room = MAX_PHOTOS - (keptPhotos.length + pendingFiles.length);
    const chosen = Array.from(photoInput.files);

    chosen.slice(0, Math.max(room, 0)).forEach((file) => {
      pendingFiles.push({ file, previewUrl: URL.createObjectURL(file) });
    });

    if (chosen.length > room) {
      errorEl.textContent = `A post can have at most ${MAX_PHOTOS} photos — extra files were skipped.`;
      errorEl.classList.remove("d-none");
    }
    // Clear the input so re-picking the same file still fires `change`.
    photoInput.value = "";
    renderPreviews();
  });

  renderPreviews();

  container.querySelector("#post-cancel").addEventListener("click", () => history.back());

  container.querySelector("#post-form").addEventListener("submit", async (e) => {
    e.preventDefault();
    const errorEl = container.querySelector("#post-error");
    errorEl.classList.add("d-none");
    const submitBtn = container.querySelector("#post-submit");
    submitBtn.disabled = true;

    const content = container.querySelector("#post-content").value.trim();
    if (!content) {
      errorEl.textContent = "Write something first.";
      errorEl.classList.remove("d-none");
      submitBtn.disabled = false;
      return;
    }

    const originalLabel = submitBtn.textContent;
    try {
      // Upload first: a failed upload should leave no post behind, so
      // nothing is written to the API until every object key is in hand.
      // Sequential rather than Promise.all so a 10-photo post doesn't
      // open ten simultaneous connections to the bucket, and so the
      // progress counter below is meaningful.
      const photoKeys = [];
      for (const [index, pending] of pendingFiles.entries()) {
        submitBtn.textContent = `Uploading photo ${index + 1} of ${pendingFiles.length}...`;
        photoKeys.push(await uploadPhoto(pending.file));
      }
      submitBtn.textContent = originalLabel;

      const body = { content };
      if (initialDivePlanId) body.dive_plan_id = Number(initialDivePlanId);
      body.dive_site_id = pickerValue(container, "post-site");
      body.community_id = pickerValue(container, "post-community");
      if (photoKeys.length) body.photo_keys = photoKeys;
      // Only meaningful when editing — tells the server which of the
      // post's existing photos survived the edit. Sent even when empty,
      // since an empty list is how "I removed them all" is expressed.
      if (editingId) body.photo_ids = keptPhotos.map((p) => p.id);
      // Left out when unchanged; null removes the post's dive.
      const dive = await diveField.value();
      if (dive !== undefined) body.dive_profile = dive;

      const post = editingId
        ? await api.patch(`/posts/${editingId}`, body)
        : await api.post("/posts", body);
      releasePreviews();
      navigate(`/post/${post.id}`);
    } catch (err) {
      submitBtn.textContent = originalLabel;
      errorEl.textContent = err instanceof ApiError ? err.message : "Something went wrong.";
      errorEl.classList.remove("d-none");
    } finally {
      submitBtn.disabled = false;
    }
  });

  // The form can arrive with an id but no name — opened from a dive
  // event, say — so the labels are filled in afterwards.
  //
  // Last, deliberately: binding the form before this await leaves no
  // window in which the markup exists unbound. A second render landing
  // mid-await would otherwise replace the form and leave both renders
  // binding to the same surviving one, posting twice.
  await Promise.all([sitePicker?.resolveInitial(), cohortPicker?.resolveInitial()]);
}
